/* eslint-disable complexity */
import * as React from "react";
import {
  LocalizeContextProps,
  Translate,
  withLocalize
} from "react-localize-redux";
import Typography from "@material-ui/core/Typography/Typography";
import { ModalDialog } from "../../ui-components/ModalDialog";
import { translationKeys } from "../../../gen/translation-keys";
import ExportDocumentOptions from "./ExportDocumentOptions";
import { mapToTreeItems, useTocData } from "../Toc/toc-helpers";
import {
  TreeViewMultiSelect,
  getAllIds,
  getNotSelectedItems,
  getRemovedItems
} from "../../ui-components/TreeView";
import { PrintRequestModel } from "@rhinestone/portal-web-api";
import { PortalApiContext } from "../../api-access/PortalApiProvider";
import { useSnackbar } from "../../ui-components/Snackbar/SnackbarProvider";
import { useDocumentInfo } from "../document-context";
import { openAsPdf } from "./export-document.util";
import { useFieldSet } from "../use-fieldset";
import { FieldNames } from "../document-fieldsets";
import { useAnnotationState } from "../annotations/use-annotation-state";
import OverlayLoader from "../../ui-components/LoadingProgress/OverlayLoader";
import { Box } from "@material-ui/core";

import makeStyles from "@material-ui/core/styles/makeStyles";

const useStyles = makeStyles(theme => ({
  container: {
    display: "flex",
    flexDirection: "column",
    maxHeight: "calc(100vh - 300px)"
  },
  tree: { overflowY: "auto", flex: "0 1 auto" },
  exportOptions: {
    flex: "1 0 auto",
    overflowY: "hidden"
  }
}));

export interface ExportDocumentDialogProps extends LocalizeContextProps {
  open: boolean;
  onClose: () => void;
}

const ExportDocumentDialog: React.FC<ExportDocumentDialogProps> = ({
  open,
  onClose,
  activeLanguage
}) => {
  const classes = useStyles();
  const [exporting, setExporting] = React.useState<boolean>(false);
  const { hiveId, fullName, annotationTarget, primaryVariantTypeCode } =
    useDocumentInfo();
  const [portalApiClient] = React.useContext(PortalApiContext);
  const { enqueueFailure } = useSnackbar();
  const { data: tocData } = useTocData();
  const documentFieldset = useFieldSet(hiveId, fullName);
  const annotationState = useAnnotationState(
    annotationTarget.annotationTargetId,
    primaryVariantTypeCode === "Schultz" ? "html" : "pdf"
  );
  const hasAnnotations = (annotationState.annotations || []).length > 0;

  const treeItemData = React.useMemo(
    () => mapToTreeItems(tocData?.entries || []),
    [tocData]
  );

  const [selectedSections, setSelectedSections] = React.useState<string[]>([]);

  const [options, setOptions] = React.useState({
    showAbstract: false,
    showComments: false,
    commentsEnabled: true,
    printToc: true,
    printCover: true
  });

  const [hasAbstract, setHasAbstract] = React.useState<boolean>(false);

  React.useEffect(() => {
    const hasAbstractField = !!(
      documentFieldset && documentFieldset[FieldNames.Abstract]
    );
    setHasAbstract(hasAbstractField);
    setOptions(prev => ({
      ...prev,
      showAbstract: hasAbstractField
    }));
  }, [documentFieldset]);

  // Initially select all nodes
  React.useEffect(
    () => setSelectedSections(getAllIds(treeItemData)),
    [treeItemData]
  );

  const getRemovedSectionIds = () =>
    getRemovedItems(selectedSections, treeItemData).map(i => i.value ?? "");

  const exportDocument = async () => {
    const annotationsContext = annotationState.context;
    const printRequest: PrintRequestModel = {
      lang: activeLanguage?.code,
      removedSectionIds: getRemovedSectionIds(),
      showAbstract: options.showAbstract,
      showCoverPage: options.printCover,
      showTableOfContents: options.printToc,
      annotationRequest:
        options.showComments && annotationsContext
          ? {
              contextId: annotationsContext.contextId,
              contextType: annotationsContext.type === "user" ? 0 : 1
            }
          : undefined
    };

    try {
      setExporting(true);

      // use ordinal fetch to get the document in PDF format, auto-generated client doesn't allow that
      const response = await fetch(
        `${portalApiClient.relativeBasePortalApiUrl()}/Print/h/${hiveId}/${fullName}`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json;charset=utf-8"
          },
          body: JSON.stringify(printRequest)
        }
      );

      if (!response.ok) {
        throw new Error("Export failed");
      }

      const buffer = await response.arrayBuffer();
      openAsPdf(buffer, fullName);
    } catch {
      enqueueFailure(
        <Translate
          id={translationKeys.document_page.export_pdf_error_message}
        />
      );
    } finally {
      setExporting(false);
    }
  };

  const updateSelection = (selected: string[]) => {
    setSelectedSections(selected);
    const allSelected =
      getNotSelectedItems(selected, treeItemData).length === 0;

    const showComments = allSelected ? options.showComments : false;
    setOptions(prev => ({
      ...prev,
      showComments,
      commentsEnabled: allSelected
    }));
  };

  return (
    <ModalDialog
      open={open}
      onClose={onClose}
      title={
        <Translate id={translationKeys.document_page.export_dialog_title} />
      }
      cancel={{
        action: onClose,
        caption: <Translate id={translationKeys.document_page.cancel} />
      }}
      confirm={{
        disabled: exporting,
        action: exportDocument,
        caption: <Translate id={translationKeys.document_page.export} />
      }}
    >
      <OverlayLoader isLoading={exporting}>
        <Box className={classes.container}>
          {
            // We don't support exporting sections for PDFs as it is not handled server-side
            primaryVariantTypeCode !== "Pdf" && (
              <>
                <Typography variant="subtitle1">
                  <Translate
                    id={translationKeys.document_page.export_document_sections}
                  />
                </Typography>
                <Box className={classes.tree}>
                  {treeItemData && treeItemData.length > 0 && (
                    <>
                      <TreeViewMultiSelect
                        selected={selectedSections}
                        onChange={selected => updateSelection(selected)}
                        items={treeItemData}
                      />
                    </>
                  )}
                </Box>
              </>
            )
          }
          <Box className={classes.exportOptions}>
            <ExportDocumentOptions
              options={options}
              onChange={setOptions}
              hasAnnotations={annotationState.isShown && hasAnnotations}
              hasAbstract={hasAbstract}
            />
          </Box>
        </Box>
      </OverlayLoader>
    </ModalDialog>
  );
};

export default withLocalize<ExportDocumentDialogProps>(ExportDocumentDialog);
